import { config } from '../config';
import { powerConfig } from '../features/dashboard/powerConfig';
import { IPermission, IRole, permissionMap } from './permissions';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { useEffect, useState } from 'react';

let msalInstance: PublicClientApplication;
let msalInitialized = false;
let authReadyPromise: Promise<void>;
let redirectPromise: Promise<void> | null = null;

/**
 * Startup auth work is shared across every caller so the app only processes the
 * MSAL redirect response once before any API, SignalR, or hook asks for an account.
 */
// Hack to ensure that MSAL is not instantiated during testing because
// `crypto` global object is not available in the JSDOM testing environment.
// Attempting to mock the crypto object did not work either because the instantiation
// occurs prior to mock getting injected.
if (import.meta.env.MODE !== 'test') {
  msalInstance = new PublicClientApplication({
    auth: {
      clientId: config.auth.clientId,
      authority: config.auth.authority,
      redirectUri: window.location.origin,
    },
  });
  authReadyPromise = msalInstance
    .initialize()
    .then(async () => {
      msalInitialized = true;
      const response = await msalInstance.handleRedirectPromise();
      setActiveAccount(
        response?.account ||
          msalInstance.getActiveAccount() ||
          msalInstance.getAllAccounts()[0] ||
          undefined,
      );
    })
    .catch((e) => {
      console.error('MSAL authentication startup failed: ', e);
      throw e;
    });
} else {
  msalInstance = {} as PublicClientApplication;
  msalInitialized = true;
  authReadyPromise = Promise.resolve();
}

/**
 * Persists the provided account as MSAL's active account when the runtime API is available.
 *
 * @param account Account selected during redirect handling or local account lookup.
 */
const setActiveAccount = (account: AccountInfo | undefined) => {
  if (account && typeof msalInstance.setActiveAccount === 'function') {
    msalInstance.setActiveAccount(account);
  }
};

/**
 * Interactive redirects should behave like a single in-flight navigation. This
 * prevents parallel login or token requests from launching multiple redirects.
 *
 * @param startRedirect Redirect starter invoked when no redirect is already in flight.
 * @returns The active redirect promise, or a newly-created one.
 */
const redirectOnce = async (startRedirect: () => Promise<void>) => {
  if (!redirectPromise) {
    redirectPromise = startRedirect().finally(() => {
      redirectPromise = null;
    });
  }

  return redirectPromise;
};

/**
 * Waits for MSAL startup and returns the selected account. If startup finishes
 * without an account, begin the login redirect flow and let the browser navigate.
 *
 * @returns The current account when available; otherwise `undefined` after login redirect begins.
 */
const ensureAccount = async (): Promise<AccountInfo | undefined> => {
  await authReadyPromise;

  const account = getAccount();
  if (!account) {
    await redirectOnce(() =>
      msalInstance.loginRedirect({ scopes: [config.api.scope] }),
    );
    return;
  }

  return account;
};

/**
 * Shared silent-token path for app and Power BI scopes. Interactive fallback is
 * reserved for MSAL's explicit interaction-required error.
 *
 * @param scopes OAuth scopes requested for the access token.
 * @returns An access token when silent acquisition succeeds; otherwise `undefined` after redirect begins.
 */
const getToken = async (scopes: string[]) => {
  const account = await ensureAccount();
  if (!account) {
    return;
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account,
    });
    return response.accessToken;
  } catch (e) {
    console.log('Error: ', e);
    if (e instanceof InteractionRequiredAuthError) {
      await redirectOnce(() => msalInstance.acquireTokenRedirect({ scopes }));
      return;
    }
    throw e;
  }
};

/**
 * Gets an access token for the CellCommCenter API.
 *
 * @returns The API access token, or `undefined` when interactive auth navigation begins.
 */
export const getAccessToken = async () => getToken([config.api.scope]);

/**
 * Gets an access token for embedded Power BI content.
 *
 * @returns The Power BI access token, or `undefined` when interactive auth navigation begins.
 */
export const getPowerBiAccessToken = async () =>
  getToken([powerConfig.api.scope, powerConfig.api.scope2]);

/**
 * Custom React hook that manages the authentication state using MSAL (Microsoft Authentication Library).
 * It initializes the account state, handles the authentication redirect response,
 * and initiates a login redirect if no user account is detected.
 *
 * @returns {AccountInfo|undefined} The current user's account object if authenticated, otherwise undefined.
 */
export const useAuth = (): AccountInfo | undefined => {
  const [account, setAccount] = useState(getAccount());
  useEffect(() => {
    if (!account) {
      ensureAccount()
        .then((currentAccount) => {
          setAccount(currentAccount);
        })
        .catch((e) => {
          console.error('Authentication initialization failed: ', e);
        });
    }
  }, [account]);

  return account;
};

/**
 * Redirects the active user through MSAL logout
 */
export const logout = () => {
  authReadyPromise.then(() => {
    msalInstance.logoutRedirect();
  });
};

/**
 * Returns the active MSAL account, falling back to the first stored account when needed.
 *
 * @returns The selected account when available; otherwise `undefined`.
 */
export const getAccount = () => {
  if (!msalInitialized || typeof msalInstance.getAllAccounts !== 'function') {
    return;
  }

  const activeAccount = msalInstance.getActiveAccount?.();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }
};

/**
 * Indicates whether the current account has the `Dev` role.
 *
 * @returns `true` when the current user is a developer; otherwise `false`.
 */
export const isDeveloper = () => {
  const account = getAccount();
  if (!account) {
    return false;
  }
  console.log('account: ', account);
  // @ts-ignore
  if (!account.idTokenClaims?.roles) {
    return false;
  }

  // @ts-ignore
  return account.idTokenClaims?.roles.includes('Dev');
};

/**
 * Gets the role list from the current account's ID token claims.
 *
 * @returns Roles assigned to the current account, or an empty array when unavailable.
 */
export const getAccountRoles = (): IRole[] => {
  const account = getAccount();
  if (!account) {
    return [];
  }
  // @ts-ignore
  if (!account?.idTokenClaims?.roles) {
    return [];
  }
  // @ts-ignore
  return account.idTokenClaims.roles as any;
};

/**
 * Checks whether current user has at least one of the specified permissions.
 *
 * @param permissions Permission keys to evaluate against the current account roles.
 * @returns `true` when the current account satisfies at least one requested permission.
 */
export const hasPermission = (permissions: IPermission[]): boolean => {
  const accountRoles: IRole[] = getAccountRoles();
  let validRoles: IRole[] = [];
  permissions.forEach((p) => validRoles.push(...permissionMap[p]));
  return accountRoles.filter((r) => validRoles.includes(r)).length > 0;
};
